import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Page configurations
  const pageConfig = {
    '/': { title: 'Home', parent: null, category: 'main' },
    '/flights': { title: 'Flights', parent: '/', category: 'search' },
    '/flights/results': { title: 'Flight Results', parent: '/flights', category: 'results' },
    '/flights/roundtrip-results': { title: 'Round Trip Results', parent: '/flights', category: 'results' },
    '/flights/:id': { title: 'Flight Details', parent: '/flights/results', category: 'details' },
    '/hotels': { title: 'Hotels', parent: '/', category: 'search' },
    '/hotels/results': { title: 'Hotel Results', parent: '/hotels', category: 'results' },
    '/hotels/:id': { title: 'Hotel Details', parent: '/hotels/results', category: 'details' },
    '/buses': { title: 'Buses', parent: '/', category: 'search' },
    '/buses/results': { title: 'Bus Results', parent: '/buses', category: 'results' },
    '/trains': { title: 'Trains', parent: '/', category: 'search' },
    '/trains/results': { title: 'Train Results', parent: '/trains', category: 'results' },
    '/holidays': { title: 'Holiday Packages', parent: '/', category: 'search' },
    '/holidays/:id': { title: 'Holiday Details', parent: '/holidays', category: 'details' },
    '/transfers': { title: 'Transfers', parent: '/', category: 'search' },
    '/login': { title: 'Login', parent: null, category: 'auth' },
    '/register': { title: 'Register', parent: null, category: 'auth' },
    '/forgot-password': { title: 'Forgot Password', parent: '/login', category: 'auth' },
    '/my-profile': { title: 'My Profile', parent: '/', category: 'dashboard' },
    '/my-trips': { title: 'My Trips', parent: '/my-profile', category: 'dashboard' },
    '/wallet': { title: 'My Wallet', parent: '/my-profile', category: 'dashboard' },
    
    '/about': { title: 'About Us', parent: '/', category: 'info' },
    '/contact': { title: 'Contact Us', parent: '/', category: 'info' },
    '/terms': { title: 'Terms & Conditions', parent: '/', category: 'legal' },
    '/privacy': { title: 'Privacy Policy', parent: '/', category: 'legal' },
    '/faq': { title: 'FAQ', parent: '/', category: 'support' },
    '/blog': { title: 'Blog', parent: '/', category: 'info' },
    '/support': { title: 'Customer Support', parent: '/', category: 'support' },
    '/help': { title: 'Booking Help', parent: '/', category: 'support' },
    '/cancellation': { title: 'Cancellation Policy', parent: '/', category: 'legal' },
    '/refund': { title: 'Refund Policy', parent: '/', category: 'legal' },
    '/insurance': { title: 'Travel Insurance', parent: '/', category: 'services' },
    '/group-booking': { title: 'Group Bookings', parent: '/', category: 'services' }
  };

  // Related pages suggestions
  const relatedPages = {
    '/flights': ['/hotels', '/transfers'],
    '/hotels': ['/flights', '/transfers'],
    '/buses': ['/trains', '/flights'],
    '/trains': ['/buses', '/flights'],
    '/holidays': ['/flights', '/hotels'],
    '/transfers': ['/flights', '/hotels']
  };

  // Update navigation state when location changes
  useEffect(() => {
    const pathname = location.pathname;
    const config = pageConfig[pathname] || { title: 'Page Not Found', parent: null, category: 'error' };
    
    setCurrentPage({ path: pathname, ...config });
    
    // Update navigation history
    setNavigationHistory(prev => {
      const newHistory = [...prev];
      if (newHistory[newHistory.length - 1]?.path !== pathname) {
        newHistory.push({ path: pathname, ...config, timestamp: Date.now() });
        // Keep only last 10 entries
        if (newHistory.length > 10) {
          newHistory.shift();
        }
      }
      return newHistory;
    });

  }, [location.pathname]);

  const goBack = () => {
    if (navigationHistory.length > 1) {
      const previousPage = navigationHistory[navigationHistory.length - 2];
      navigate(previousPage.path);
    } else {
      navigate('/');
    }
  };

  const getRelatedPages = (pathname) => {
    return relatedPages[pathname] || [];
  };

  const getPageCategory = (pathname) => {
    return pageConfig[pathname]?.category || 'unknown';
  };

  const value = {
    navigationHistory,
    currentPage,
    goBack,
    pageConfig
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};








